import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Package, Trash2, ShoppingCart, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const Cart = () => {
  const { user } = useContext(AuthContext);
  const [cart, setCart] = useState({ items: [] });
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCartAndProducts();
  }, []);

  const fetchCartAndProducts = async () => {
    try {
      const [cartResponse, productsResponse] = await Promise.all([
        axios.get(`${API}/cart`),
        axios.get(`${API}/products`)
      ]);
      
      setCart(cartResponse.data);
      
      const productsMap = {};
      productsResponse.data.forEach(p => {
        productsMap[p.id] = p;
      });
      setProducts(productsMap);
    } catch (error) {
      console.error('Error fetching cart:', error);
      toast.error('Failed to load cart');
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, newQuantity) => {
    if (newQuantity < 1) return;
    
    const newItems = cart.items.map(item => 
      item.product_id === productId 
        ? { ...item, quantity: newQuantity }
        : item
    );
    
    try {
      await axios.post(`${API}/cart`, { 
        user_email: user.email,
        items: newItems 
      });
      setCart({ ...cart, items: newItems });
    } catch (error) {
      console.error('Error updating cart:', error);
      toast.error('Failed to update cart');
    }
  };

  const removeItem = async (productId) => {
    const newItems = cart.items.filter(item => item.product_id !== productId);
    
    try {
      await axios.post(`${API}/cart`, { 
        user_email: user.email,
        items: newItems 
      });
      setCart({ ...cart, items: newItems });
      toast.success('Item removed from cart');
    } catch (error) {
      console.error('Error removing item:', error);
      toast.error('Failed to remove item');
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart`);
      setCart({ items: [] });
      toast.success('Cart cleared');
    } catch (error) {
      console.error('Error clearing cart:', error);
      toast.error('Failed to clear cart');
    }
  };

  const calculateTotals = () => {
    let totalCases = 0;
    let subtotal = 0;
    
    cart.items?.forEach(item => {
      const product = products[item.product_id];
      if (product) {
        totalCases += item.quantity;
        subtotal += product.price_per_case * item.quantity;
      }
    });
    
    let discountPercent = 0;
    if (totalCases >= 51) discountPercent = 10;
    else if (totalCases >= 11) discountPercent = 5;
    
    const discountAmount = subtotal * (discountPercent / 100);
    const total = subtotal - discountAmount;
    
    return { totalCases, subtotal, discountPercent, discountAmount, total };
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="text-xl text-gray-600">Loading cart...</div>
        </div>
      </div>
    );
  }

  const { totalCases, subtotal, discountPercent, discountAmount, total } = calculateTotals();

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Shopping Cart</h1>
          <p className="text-lg text-gray-600">{cart.items?.length || 0} item(s) in your cart</p>
        </div>

        {cart.items?.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center" data-testid="empty-cart">
            <ShoppingCart className="h-20 w-20 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
            <p className="text-gray-600 mb-6">Add some products to get started!</p>
            <Button 
              onClick={() => navigate('/products')}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="shop-now-btn"
            >
              Shop Now
            </Button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map(item => {
                const product = products[item.product_id];
                if (!product) return null;
                
                return (
                  <div key={item.product_id} className="bg-white rounded-xl shadow-md p-6" data-testid={`cart-item-${item.product_id}`}>
                    <div className="flex gap-6">
                      <div className="bg-white rounded-lg w-24 h-24 flex items-center justify-center flex-shrink-0 border-2 border-gray-200 overflow-hidden">
                        {product.image_url ? (
                          <img 
                            src={product.image_url} 
                            alt={product.name}
                            className="w-full h-full object-contain p-2"
                          />
                        ) : (
                          <Package className="h-12 w-12 text-blue-600" />
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">{product.name}</h3>
                            <p className="text-sm text-gray-600">{product.case_quantity} cups per case</p>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => removeItem(item.product_id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`remove-item-${item.product_id}`}
                          >
                            <Trash2 className="h-5 w-5" />
                          </Button>
                        </div>
                        
                        <div className="flex items-center justify-between mt-4">
                          <div className="flex items-center gap-3">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                              data-testid={`decrease-qty-${item.product_id}`}
                            >
                              -
                            </Button>
                            <span className="font-semibold text-lg w-12 text-center" data-testid={`quantity-${item.product_id}`}>
                              {item.quantity}
                            </span>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                              data-testid={`increase-qty-${item.product_id}`}
                            >
                              +
                            </Button>
                          </div>
                          
                          <div className="text-right">
                            <p className="text-sm text-gray-600">${product.price_per_case.toFixed(2)} per case</p>
                            <p className="text-xl font-bold text-gray-900" data-testid={`item-total-${item.product_id}`}>
                              ${(product.price_per_case * item.quantity).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              <Button 
                variant="outline" 
                onClick={clearCart}
                className="w-full text-red-600 border-red-300 hover:bg-red-50"
                data-testid="clear-cart-btn"
              >
                Clear Cart
              </Button>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-md p-6 sticky top-24" data-testid="order-summary">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Cases</span>
                    <span className="font-semibold" data-testid="total-cases">{totalCases}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-semibold" data-testid="subtotal">${subtotal.toFixed(2)}</span>
                  </div>
                  
                  {discountPercent > 0 && (
                    <div className="bg-green-50 border-2 border-green-200 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-green-700 font-semibold">Bulk Discount ({discountPercent}%)</span>
                        <span className="text-green-700 font-bold" data-testid="discount-amount">-${discountAmount.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="border-t-2 border-gray-200 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold text-gray-900">Total</span>
                      <span className="text-2xl font-bold text-blue-600" data-testid="total-price">${total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                
                {discountPercent === 0 && totalCases < 11 && (
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 mb-6">
                    <p className="text-sm text-blue-700">
                      Add {11 - totalCases} more case(s) to get 5% off!
                    </p>
                  </div>
                )}
                
                {discountPercent === 5 && totalCases < 51 && (
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 mb-6">
                    <p className="text-sm text-blue-700">
                      Add {51 - totalCases} more case(s) to get 10% off!
                    </p>
                  </div>
                )}
                
                <Button 
                  onClick={handleCheckout}
                  size="lg"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white text-lg py-6"
                  data-testid="proceed-to-checkout-btn"
                >
                  Proceed to Checkout <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Cart;